import { getFrontendMenus } from '@/api/menu'
import { REQUEST_SUCCESS_CODE } from '@/config/constant'

export interface MenuItem {
    id: number
    name: string
    icon: string
    path: string
    linkType: number
    openMode: number
    externalUrl: string
    tag: string
    requireAuth: boolean
    children: MenuItem[]
}

export interface MenuGroup {
    code: string
    sort: number
    menus: MenuItem[]
}

export interface MenuState {
    groups: MenuGroup[]
    loaded: boolean
}

const CACHE_KEY = 'web_menu_cache'

const FALLBACK_MENUS: MenuGroup[] = [
    {
        code: 'core',
        sort: 1,
        menus: [
            {
                id: 1,
                name: 'Ask Sovi',
                icon: 'chat',
                path: '/chat',
                linkType: 1,
                openMode: 1,
                externalUrl: '',
                tag: '',
                requireAuth: false,
                children: [
                    { id: 9, name: 'Video Explanation', icon: '', path: '/video', linkType: 1, openMode: 2, externalUrl: '', tag: '', requireAuth: false, children: [] },
                    { id: 13, name: 'Expert Help', icon: '', path: '/expert', linkType: 1, openMode: 2, externalUrl: '', tag: '', requireAuth: false, children: [] }
                ]
            },
            {
                id: 2,
                name: 'AI Study',
                icon: 'study',
                path: '/study',
                linkType: 1,
                openMode: 1,
                externalUrl: '',
                tag: '',
                requireAuth: false,
                children: [
                    { id: 3, name: 'Chat PDF', icon: '', path: '/study?type=knowledge', linkType: 1, openMode: 1, externalUrl: '', tag: '', requireAuth: false, children: [] },
                    { id: 4, name: 'Quiz Generator', icon: '', path: '/study?type=quiz', linkType: 1, openMode: 1, externalUrl: '', tag: '', requireAuth: false, children: [] }
                ]
            },
            { id: 5, name: 'AP Test Prep', icon: 'testprep', path: '/apexam', linkType: 1, openMode: 1, externalUrl: '', tag: '', requireAuth: false, children: [] }
        ]
    },
    {
        code: 'resource',
        sort: 2,
        menus: [
            { id: 6, name: 'Resources', icon: 'resource', path: '/resources/category/math', linkType: 1, openMode: 1, externalUrl: '', tag: '', requireAuth: false, children: [] },
            { id: 7, name: 'Blog', icon: 'blog', path: '/blog', linkType: 1, openMode: 1, externalUrl: '', tag: '', requireAuth: false, children: [] },
            { id: 14, name: 'Search', icon: 'search', path: '/search', linkType: 1, openMode: 1, externalUrl: '', tag: '', requireAuth: false, children: [] }
        ]
    },
    {
        code: 'info',
        sort: 3,
        menus: [{ id: 8, name: 'App', icon: 'app', path: '/app', linkType: 1, openMode: 1, externalUrl: '', tag: '', requireAuth: false, children: [] }]
    }
]

function loadCache(): MenuGroup[] | null {
    try {
        const raw = localStorage.getItem(CACHE_KEY)
        if (raw) {
            return JSON.parse(raw) as MenuGroup[]
        }
    } catch {
        // ignore
    }
    return null
}

function saveCache(groups: MenuGroup[]) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(groups))
    } catch {
        // ignore
    }
}

const state: MenuState = {
    groups: [],
    loaded: false
}

const getters = {
    groups: (state: MenuState) => state.groups,
    loaded: (state: MenuState) => state.loaded,
    flatMenus: (state: MenuState): MenuItem[] => {
        const result: MenuItem[] = []
        const flatten = (items: MenuItem[]) => {
            for (const item of items) {
                result.push(item)
                if (item.children?.length) {
                    flatten(item.children)
                }
            }
        }
        for (const group of state.groups) {
            flatten(group.menus)
        }
        return result
    },
    iframeMenus: (state: MenuState, getters: any): MenuItem[] => {
        return getters.flatMenus.filter((m: MenuItem) => m.openMode === 3)
    }
}

function prefixIframePaths(groups: MenuGroup[]) {
    const processItems = (items: MenuItem[]) => {
        for (const item of items) {
            if (item.openMode === 3 && item.path) {
                let path = item.path.startsWith('/') ? item.path : '/' + item.path
                if (!path.startsWith('/page_in')) {
                    if (!item.externalUrl) {
                        item.externalUrl = path
                    }
                    item.path = '/page_in' + path
                }
                
                // Convert production domains to same-origin relative paths to allow iframe local dev without cross-origin issues
                if (item.externalUrl && (item.externalUrl.includes('mysovi.ai') || item.externalUrl.includes('sovi.ai'))) {
                    try {
                        const urlObj = new URL(item.externalUrl)
                        item.externalUrl = urlObj.pathname + urlObj.search + urlObj.hash
                    } catch {
                        // ignore
                    }
                }
            }
            if (item.children?.length) {
                processItems(item.children)
            }
        }
    }
    for (const group of groups) {
        if (group.menus?.length) {
            processItems(group.menus)
        }
    }
}

const mutations = {
    SET_MENUS(state: MenuState, groups: MenuGroup[]) {
        prefixIframePaths(groups)
        state.groups = groups
        state.loaded = true
    }
}

const actions = {
    /**
     * 同步加载本地缓存，立即可用
     */
    loadLocal({ commit }: any) {
        const cached = loadCache()
        if (cached && cached.length > 0) {
            commit('SET_MENUS', cached)
        } else {
            commit('SET_MENUS', FALLBACK_MENUS)
        }
    },

    /**
     * 异步从服务端拉取最新菜单，有变更时更新 store + localStorage
     */
    async syncRemote({ commit, state }: any) {
        try {
            const res = await getFrontendMenus()
            if (res.code == REQUEST_SUCCESS_CODE && res.data?.groups) {
                const remote = res.data.groups as MenuGroup[]
                const remoteJson = JSON.stringify(remote)
                const localJson = JSON.stringify(state.groups)
                if (remoteJson !== localJson) {
                    commit('SET_MENUS', remote)
                    saveCache(remote)
                }
            }
        } catch (error) {
            console.error('Failed to fetch menus:', error)
        }
    }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}
